import { useMemo } from 'react';
import { useProcessingDataExportDocument } from 'src/suitability-report/hooks/useProcessingDataExportDocument';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';
import useTemplates, {
  type TemplateWithoutSections,
} from 'src/data-capture/hooks/templates/useTemplates';

type SuitabilityReportType = 'NO_CHANGE';

type SuitabilityReport = {
  id: string;
  clientId: string;
  backgroundProcessId?: string;
  backgroundProcess?: BackgroundProcess;
  reportType: SuitabilityReportType;
  createdAt: string;
  currentSnapshotId?: string;
  previousSnapshotId?: string;
  templateId?: string;
  supportingDocumentIds: string[];
  reportFileId?: number;
  snapshotHashes: string[];
  uniqueHash: string;
  hasTitlePage: boolean;
  hasTableOfContents: boolean;
  usesDeepThinking: boolean;
  displayName: string;
  isGenerated: true;
};

type Props = {
  clientId?: string;
};

export const useSuitabilityReports = ({ clientId }: Props = {}) => {
  const { items, isLoading, isError, refetch, error } = useProcessingDataExportDocument<
    Omit<SuitabilityReport, 'displayName' | 'isGenerated'>
  >({
    clientId,
    resource: 'suitability-reports',
  });

  const { files: templates, isLoading: isTemplatesLoading } = useTemplates({
    templateType: 'SUITABILITY_REPORT',
  });

  const toFriendlyLabel = (fileName?: string) => {
    if (!fileName) return undefined;
    const base = fileName.replace(/\.[^/.]+$/, '');
    const spaced = base.replace(/[_-]+/g, ' ').toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  };

  const labelFromTemplate = (template?: TemplateWithoutSections) => {
    if (!template) return undefined;
    const name = template.displayName?.trim();
    if (name) return name;
    return toFriendlyLabel(template.file?.fileName);
  };

  const suitabilityReports = useMemo(
    () =>
      items.map(report => {
        const template = templates.find(t => t.id === report.templateId);
        const displayName = labelFromTemplate(template);
        return {
          ...report,
          isGenerated: true,
          displayName:
            displayName ||
            // Fallback: derive from report type as before
            report.reportType
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b\w/g, char => char.toUpperCase()),
        };
      }),
    [items, templates],
  );

  return {
    suitabilityReports,
    isLoading: isLoading || isTemplatesLoading,
    isError,
    error,
    refetch,
  };
};
